import { Stack } from "@mui/material";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import {
  EnvelopeOpenTextIcon,
  MobileButtonScreenIcon,
  PrimaryToast,
  StyledDivider,
  phoneNumberMasker,
} from "react-common-ui-library";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import {
  EMAIL,
  EMAIL_ICON_ID,
  PHONE,
  PHONE_ICON_ID,
} from "../../../Constants/LoginVerificationMethodsConstants";
import {
  resetAddEditVerificationValues,
  resetIsAddEditVerificationFlow,
  resetToastFlow,
} from "../../../Features/LoginSlice";
import { getMyProfile } from "../../../Features/MyProfileSlice";
import { manageLoginSelector } from "../../../Selectors/LoginSelector";
import { LoginVerificationMethodsContactWrapper } from "../../../Styles/Login/LoginVerificationMethods/LoginVerificationMethodsStyles";
import { SecurityPreferencesHeader } from "../../MyProfile/SecurityPreferences/SecurityPreferencesHeader";
import LoginVerificationMethodsCheckbox from "./LoginVerificationMethodsCheckbox";
import LoginVerificationMethodsHeading from "./LoginVerificationMethodsHeading";
import LoginVerificationMethodsPhoneEmailSection from "./LoginVerificationMethodsPhoneEmailSection";
import { manageMyProfileSelector } from "../../../Selectors/MyProfileSelector";

/**
 * @description Login Verification Methods screen allows the user to add, edit or remove verification methods
 * @returns {ReactNode} Renders the heading, subtext, verification method details and action buttons
 */

export const LoginVerificationMethods = ({ isProfileRoute }) => {
  const { toastFlow } = useSelector((state) => manageLoginSelector(state));

  const { preferredVerificationMethod, phoneNumber, email } = useSelector(
    (state) => manageMyProfileSelector(state)
  );

  const intl = useIntl();
  const dispatch = useDispatch();

  const [showSuccessToast, setShowSuccessToast] = useState(false);

  const handleOnToastClose = () => {
    setShowSuccessToast(false);
    dispatch(resetToastFlow());
  };

  useEffect(() => {
    if (toastFlow) {
      setShowSuccessToast(true);
    }
  }, [toastFlow, dispatch]);

  useEffect(() => {
    dispatch(getMyProfile());
    dispatch(resetAddEditVerificationValues());
    dispatch(resetIsAddEditVerificationFlow());
  }, [dispatch, isProfileRoute]);

  return (
    <Stack spacing={{ xs: 4, md: 6 }}>
      {isProfileRoute ? (
        <SecurityPreferencesHeader />
      ) : (
        <LoginVerificationMethodsHeading />
      )}
      <Stack spacing={{ xs: 4, md: 5 }}>
        <LoginVerificationMethodsContactWrapper>
          <LoginVerificationMethodsPhoneEmailSection
            phoneEmailValue={phoneNumber ? phoneNumberMasker(phoneNumber) : ""}
            emailOrPhoneIcon={<MobileButtonScreenIcon id={PHONE_ICON_ID} />}
            preferredContact={preferredVerificationMethod === PHONE}
            isPhone={true}
          />
          <StyledDivider />
          <LoginVerificationMethodsPhoneEmailSection
            phoneEmailValue={email}
            emailOrPhoneIcon={<EnvelopeOpenTextIcon id={EMAIL_ICON_ID} />}
            preferredContact={preferredVerificationMethod === EMAIL}
            isPhone={false}
          />
        </LoginVerificationMethodsContactWrapper>
        <LoginVerificationMethodsCheckbox isProfileRoute={isProfileRoute} />
      </Stack>

      {toastFlow ? (
        <PrimaryToast
          isOpen={showSuccessToast && toastFlow}
          setIsOpen={handleOnToastClose}
          id={toastFlow}
        >
          {intl.formatMessage({ id: toastFlow })}
        </PrimaryToast>
      ) : null}
    </Stack>
  );
};

LoginVerificationMethods.defaultProps = {
  isProfileRoute: false,
};

LoginVerificationMethods.propTypes = {
  isProfileRoute: PropTypes.bool,
};

export default LoginVerificationMethods;
